// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Modal, Text } from '@mantine/core';
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { BaseDocumentContextProps, DocumentTemplateDto, PositionDto } from '../DocumentT';
import { BookitupDocumentType } from '../../Types/BookitupDocument';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';
import { PositionS } from '../PositionS';
import { DocumentTemplateS } from '../DocumenteTemplateS';
import { PositionGroupS } from '../PositionGroupS';
import PositionModal from '../../Molecules/Document/Position/PositionModalV2';
import { openDeletePositionGroupModal } from '../../Molecules/Document/Position/PositionGroupModals';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface DocumentTemplateContextProps extends BaseDocumentContextProps<DocumentTemplateDto> {}

export const DocumentTemplateContext = React.createContext<DocumentTemplateContextProps>(
  {} as DocumentTemplateContextProps,
);

export const DocumentTemplateContextProvider = () => {
  const { id, type } = useParams();
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState<DocumentTemplateDto | null>(null);
  const [selectedPositionId, selectPosition] = useState<number>();
  const [opened, { toggle }] = useDisclosure(false);
  const { id: templateId } = template ?? {};
  const [addingGroupAtIndex, setAddingGroupAtIndex] = useState<number | null>(null);
  const [movingGroup, setMovingGroup] = useState(false);
  const [positionInsertionGroupId, setPositionInsertionGroupId] = useState<string | null>(null);

  useEffect(() => {
    if (id && type) {
      setLoading(true);
      DocumentTemplateS.fetchTemplate(type as BookitupDocumentType, id)
        .then(setTemplate)
        .finally(() => setLoading(false));
    }
  }, [id, type]);

  const refresh = async () => {
    if (type && id) {
      const resJSON = await DocumentTemplateS.fetchTemplate(type as BookitupDocumentType, id);
      if (resJSON) {
        setTemplate(resJSON);
      }
    }
  };

  const handleChange = (patch: Partial<DocumentTemplateDto>) => {
    setTemplate((prev) => {
      if (prev) {
        return { ...prev, ...patch };
      }
      return null;
    });
  };

  const savePatch = async (patch: Partial<DocumentTemplateDto>) => {
    if (templateId) {
      const resJSON = await DocumentTemplateS.edit(Number(templateId), patch);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const patchPosition = async (positionId: number, patch: Partial<PositionDto>) => {
    const resJSON = await PositionS.patchPosition(positionId, patch);
    if (resJSON) {
      // Refresh document to properly update all tax/sum values
      refresh();
      return true;
    }
    return false;
  };

  const movePosition = async (positionId: number, targetPositionId: number) => {
    if (templateId) {
      const resJSON = await DocumentTemplateS.movePosition(Number(templateId), positionId, targetPositionId);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const deletePosition = async (positionId: number) => {
    const onConfirm = async () => {
      if (templateId) {
        const resJSON = await DocumentTemplateS.removePosition(Number(templateId), positionId);
        if (resJSON) {
          setTemplate(resJSON);
          return true;
        }
      }
      return false;
    };
    openDeleteConfirmModal({
      title: 'Wirklich löschen',
      description: <Text>Möchten Sie diese Position wirklich entfernen?</Text>,
      onConfirm,
    });
  };

  const addPosition = async (targetPositionId?: number, position?: PositionDto) => {
    if (templateId) {
      const resJSON = await DocumentTemplateS.insertPosition(Number(templateId), targetPositionId, position);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const addGroup = async (index?: number) => {
    if (templateId) {
      setAddingGroupAtIndex(index ?? template?.positionGroups.length ?? 0);
      try {
        const resJSON = await PositionGroupS.createForTemplate(templateId, index);
        if (resJSON) {
          handleChange({ positionGroups: resJSON });
          return true;
        }
      } finally {
        setAddingGroupAtIndex(null);
      }
    }
    return false;
  };

  const moveGroup = async (from: number, to: number) => {
    if (templateId) {
      setMovingGroup(true);
      try {
        const resJSON = await PositionGroupS.moveTemplate(templateId, from, to);
        if (resJSON) {
          setTemplate(resJSON);
          return true;
        }
      } finally {
        setMovingGroup(false);
      }
    }
    return false;
  };

  const insertPositionIntoGroup = async (groupId: string, positionId: number, targetPositionId?: number) => {
    if (templateId) {
      setPositionInsertionGroupId(groupId);
      try {
        const resJSON = await PositionGroupS.insertPositionTemplate(groupId, positionId, templateId, targetPositionId);
        if (resJSON) {
          setTemplate(resJSON);
          return true;
        }
      } finally {
        setPositionInsertionGroupId(null);
      }
    }
    return false;
  };

  const insertNewPositionIntoGroup = async (groupId: string, product?: PositionDto, targetPositionId?: number) => {
    if (templateId) {
      const resJSON = await PositionGroupS.insertNewPositionTemplate(groupId, templateId, targetPositionId, product);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const removePositionFromGroup = async (positionId: number) => {
    if (templateId) {
      const resJSON = await PositionGroupS.deletePositionTemplate(positionId, templateId);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const removeGroup = async (groupId: string, label: string) => {
    if (template && templateId) {
      const onConfirm = async (removePositions: boolean) => {
        const resJSON = await PositionGroupS.removeFromTemplate(groupId, templateId, removePositions);
        if (resJSON) {
          setTemplate(resJSON);
          return true;
        }
        return false;
      };
      openDeletePositionGroupModal({
        label,
        onConfirm,
        positionsPresent: template.positions.filter((p) => p.groupId === groupId).length > 0,
      });
    }
  };

  const swapPositions = async (direction: string, positionId: number) => {
    if (templateId) {
      const resJSON = await DocumentTemplateS.swapPositions(templateId, direction, positionId);
      if (resJSON) {
        setTemplate(resJSON);
      }
    }
  };

  const openPositionModal = (position: PositionDto) => {
    selectPosition(position.id);
    toggle();
  };

  const addProducts = async (productIds: number[], targetPositionId?: number) => {
    if (templateId) {
      const resJSON = await DocumentTemplateS.insertProducts(templateId, productIds, targetPositionId);
      if (resJSON) {
        setTemplate(resJSON);
        return true;
      }
    }
    return false;
  };

  const value = useMemo(
    () => ({
      bookitupDocument: template,
      loading,
      refresh,
      handleChange,
      savePatch,
      positionHandlers: {
        patchPosition,
        movePosition,
        deletePosition,
        addPosition,
        swapPositions,
        openPositionModal,
        insertPositionIntoGroup,
        removePositionFromGroup,
        addProducts,
      },
      groupHandlers: {
        addGroup,
        moveGroup,
        insertPositionIntoGroup,
        insertNewPositionIntoGroup,
        removeGroup,
        addingGroupAtIndex,
        movingGroup,
        positionInsertionGroupId,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template, loading, opened, selectedPositionId, addingGroupAtIndex, movingGroup, positionInsertionGroupId],
  );

  return (
    <DocumentTemplateContext.Provider value={value}>
      {selectedPositionId && (
        <Modal
          closeOnClickOutside={false}
          transition="slide-down"
          opened={opened}
          onClose={() => {
            selectPosition(undefined);
            toggle();
          }}
          title={
            <Text size="xl" weight="bolder">
              Position
            </Text>
          }
        >
          <PositionModal
            bookitupDocument={template}
            position={template?.positions.find((p) => p.id === selectedPositionId) as PositionDto}
            savePatch={(patch) => patchPosition(selectedPositionId, patch)}
            refresh={refresh}
          />
        </Modal>
      )}
      <Outlet />
    </DocumentTemplateContext.Provider>
  );
};
